import React, {FC, useState} from 'react';
import {useIMask} from 'react-imask';
import {dependentSexOptions, Dependent, normalizeDependentSex} from './types';
import {
	Button,
	Checkbox,
	FormControl,
	FormHelperText,
	FormLabel,
	HStack,
	Input,
	Select,
	VStack,
} from 'components/ui/chakra';

interface DependentFormProps {
	dependent?: Dependent;
	onClose?: () => void;
	onSubmit: (dependent: Dependent) => void;
}

const DependentForm: FC<DependentFormProps> = ({dependent, onClose, onSubmit}) => {
	const [firstName, setFirstName] = useState(dependent?.first_name ?? '');
	const [lastName, setLastName] = useState(dependent?.last_name ?? '');
	const [dateOfBirth, setDateOfBirth] = useState(dependent?.date_of_birth ?? '');
	const [email, setEmail] = useState(dependent?.email ?? '');
	const [cellPhone, setCellPhone] = useState(dependent?.cell_phone ?? '');
	const [sex, setSex] = useState(normalizeDependentSex(dependent?.sex ?? dependent?.gender));
	const [isCoverageAttested, setIsCoverageAttested] = useState(Boolean(dependent?.id));
	const {ref: cellPhoneRef} = useIMask(
		{
			mask: '(000) 000-0000',
		},
		{
			onAccept: (value: unknown) => {
				setCellPhone(value as string);
			},
		},
	);

	const handleClose = () => {
		onClose?.();
	};

	const isFormReady =
		firstName && lastName && dateOfBirth && sex && (dependent?.id || isCoverageAttested);

	return (
		<form
			onSubmit={(e) => {
				e.preventDefault();
				onSubmit({
					id: dependent?.id,
					first_name: firstName,
					last_name: lastName,
					date_of_birth: dateOfBirth,
					email,
					cell_phone: cellPhone,
					sex,
					...(dependent?.id ? {} : {is_coverage_attested: isCoverageAttested}),
				});
			}}
		>
			<VStack
				w='full'
				justifyContent='stretch'
				mt={4}
				p={4}
				spacing={4}
				borderRadius='md'
				border='solid 1px'
				borderColor='gray.200'
				bgColor='white'
			>
				<FormControl isRequired>
					<FormLabel fontSize='sm' color='gray.500'>
						First Name
					</FormLabel>
					<Input
						type='text'
						bgColor={!firstName ? 'yellow.100' : 'white'}
						value={firstName}
						onChange={(e) => setFirstName(e.target.value)}
					/>
				</FormControl>

				<FormControl isRequired>
					<FormLabel fontSize='sm' color='gray.500'>
						Last Name
					</FormLabel>
					<Input
						type='text'
						bgColor={!lastName ? 'yellow.100' : 'white'}
						value={lastName}
						onChange={(e) => setLastName(e.target.value)}
					/>
				</FormControl>

				<FormControl isRequired>
					<FormLabel fontSize='sm' color='gray.500'>
						Date of Birth
					</FormLabel>
					<Input
						type='date'
						bgColor={!dateOfBirth ? 'yellow.100' : 'white'}
						value={dateOfBirth}
						onChange={(e) => setDateOfBirth(e.target.value)}
					/>
				</FormControl>

				<FormControl isRequired>
					<FormLabel fontSize='sm' color='gray.500'>
						Sex
					</FormLabel>
					<Select value={sex} onChange={(e) => setSex(e.target.value)}>
						{dependentSexOptions.map((option) => (
							<option key={option.value} value={option.value}>
								{option.label}
							</option>
						))}
					</Select>
				</FormControl>

				<FormControl>
					<FormLabel fontSize='sm' color='gray.500'>
						Email (Optional)
					</FormLabel>
					<Input
						type='email'
						value={email}
						onChange={(e) => setEmail(e.target.value)}
						isDisabled={!!dependent?.id && !!dependent.email}
					/>
				</FormControl>

				<FormControl>
					<FormLabel fontSize='sm' color='gray.500'>
						Cell Phone (Optional)
					</FormLabel>
					<Input type='tel' ref={cellPhoneRef as React.RefObject<HTMLInputElement>} />
				</FormControl>

				{!dependent?.id && (
					<FormControl isRequired>
						<Checkbox
							isChecked={isCoverageAttested}
							onChange={(e) => setIsCoverageAttested(Boolean(e.target.checked))}
						>
							I attest this dependent is covered under my employer&apos;s health plan.
						</Checkbox>
						<FormHelperText>
							Only add dependents who are currently eligible under your plan.
						</FormHelperText>
					</FormControl>
				)}

				<HStack w='full' justifyContent='flex-end'>
					<Button variant='ghost' onClick={handleClose}>
						Cancel
					</Button>
					<Button colorScheme='blue' type='submit' isDisabled={!isFormReady}>
						{dependent ? 'Update' : 'Add'}
					</Button>
				</HStack>
			</VStack>
		</form>
	);
};

export default DependentForm;
